import { useMemo } from 'react'

import type { AllListingsFilters } from '@b-stock/search-api-client'

import { createCategoricalAppliedComponent } from '@/components/listings/search/SearchFilters/shared/filterUtils'
import HierarchicalCategoricalFilter, {
  type KeySet,
} from '@/components/listings/search/SearchFilters/shared/HierarchicalCategoricalFilter'
import {
  buildRegionTree,
  getSelectAllKeys,
  isRegionItemCoveredBySet,
} from '@/components/listings/search/SearchFilters/shared/regionFilterUtils'
import type {
  ListingFilter,
  FilterProps,
} from '@/components/listings/search/SearchFilters/types'

const RegionFilter: ListingFilter<KeySet> = ({
  items = [],
  onChange,
  value,
}: FilterProps<KeySet>) => {
  // nodes regardless of count
  const allNodes = useMemo(
    () => buildRegionTree(items as AllListingsFilters['region']),
    [items]
  )
  // nodes we want to display, count > 0
  const displayNodes = useMemo(
    () => buildRegionTree(items as AllListingsFilters['region'], true),
    [items]
  )
  return (
    <HierarchicalCategoricalFilter
      label="Region"
      allNodes={allNodes}
      displayNodes={displayNodes}
      selected={value}
      setSelected={onChange}
    />
  )
}

RegionFilter.label = 'Filters.location'

RegionFilter.expandedOnInit = true
RegionFilter.getOwnProps = ({ state, data, updateFilters }) => {
  return {
    filterKey: 'region',
    items: data?.allFilters.region || [],
    value: new Set(state.filters.region),
    onChange: (value: Set<string>) => updateFilters({ region: [...value] }),
  }
}

RegionFilter.getSelectAllValues = (items) => {
  const rawItems = items as AllListingsFilters['region']
  const allNodes = buildRegionTree(rawItems)
  const displayNodes = buildRegionTree(rawItems, true)
  return getSelectAllKeys(displayNodes, allNodes)
}

RegionFilter.isItemCoveredBySelectedSet = isRegionItemCoveredBySet

RegionFilter.Applied = createCategoricalAppliedComponent(
  'region',
  RegionFilter.label,
  (region: string) => <span>{region}</span>
)

export default RegionFilter
